import React from "react";
import { Button, Space, Popconfirm, Switch, Tag, Tooltip, Flex, Typography } from "antd";
import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import EditableLabel from "../../../../../ui/EditableLabel";
import {
  MAX_HEADING_LENGTH,
  sanitizeAlphanumericName,
} from "../../../../../../domain/clinicalForms";
import {
  isIaForm,
  isReviewForm,
} from "../../../../../../mappers/clinicalForms.mappers";

const activeStyle = {
  background: "var(--drf-primary-tint)",
  color: "var(--drf-primary)",
  borderColor: "var(--drf-primary-border)",
};

const errorStyle = {
  borderColor: "#ff4d4f",
  color: "#ff4d4f",
};

const SectionTabs = ({
  sections,
  activeId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  onToggleRepeatable,
  formVersion,
  form,
  erroredSectionIds,
}) => {
  const activeSection = sections.find((s) => s.id === activeId);
  const currentRepeatable = !!form?.repeatable;
  const repeatableLocked = isIaForm(form) || isReviewForm(form);

  return (
    <Flex justify="space-between" align="center" gap={12} wrap>
      <Space wrap size={8}>
        {sections.map((section) => {
          const isActive = section.id === activeId;
          const canDelete = isActive && sections.length > 1;
          const hasError = erroredSectionIds?.has(section.id);
          return (
            <Button
              key={section.id}
              onClick={() => onSelect(section.id)}
              style={
                isActive
                  ? { ...activeStyle, ...(hasError ? errorStyle : {}) }
                  : hasError
                    ? errorStyle
                    : undefined
              }
            >
              <Space size={4}>
                <EditableLabel
                  value={section.title}
                  editable={isActive}
                  showIcon={isActive}
                  maxLength={MAX_HEADING_LENGTH}
                  sanitize={sanitizeAlphanumericName}
                  onChange={(name) => onRename?.(section.id, name)}
                />
                {canDelete && (
                  <Popconfirm
                    title="Delete this section?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onDelete?.(section.id)}
                  >
                    <DeleteOutlined
                      onClick={(e) => e.stopPropagation()}
                      style={{ opacity: 0.6 }}
                    />
                  </Popconfirm>
                )}
              </Space>
            </Button>
          );
        })}
        <Button type="dashed" icon={<PlusOutlined />} onClick={onAdd}>
          Add Section
        </Button>
      </Space>

      {activeSection && (
        <Space size={12}>
          <Space size={6}>
            <Typography.Text>Is Repeatable</Typography.Text>
            <Tooltip title="When enabled, this form can be submitted multiple times per admission.">
              <InfoCircleOutlined style={{ color: "#98a2b3" }} />
            </Tooltip>
            <Switch
              size="small"
              checked={currentRepeatable}
              disabled={repeatableLocked}
              onChange={(checked) =>
                onToggleRepeatable?.(activeSection.id, checked)
              }
            />
          </Space>
          <Tag color="success" style={{ borderRadius: 999, padding: "2px 12px" }}>
            V{formVersion ?? activeSection.version ?? 1}
          </Tag>
        </Space>
      )}
    </Flex>
  );
};

export default SectionTabs;
